import React, { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  Truck, MapPin, KeyRound, Eye, EyeOff, Loader2, CheckCircle2, XCircle, ShieldCheck, Ban, ToggleLeft,
} from "lucide-react";

// =============================================================================
// Gate B2 blocker-resolution — Local Delivery admin UI (Phase 3A.6).
// -----------------------------------------------------------------------------
// First-ever UI for TWO existing-but-never-surfaced APIs:
//   GET/PUT /api/retail/delivery-config                       (Phase 3A.5 —
//     tenant pickup origin, address-validation flags; existed with no UI)
//   GET     /api/retail/local-delivery/provider-config/uber-direct
//   PUT     /api/retail/local-delivery/provider-config/uber-direct/credentials
//   PUT     /api/retail/local-delivery/provider-config/uber-direct/flags
//   DELETE  /api/retail/local-delivery/provider-config/uber-direct
//     (new this milestone — TEST-only, dispatch is never a control here)
//
// This page NEVER asks for or displays a live/production credential, NEVER
// preloads a saved secret's value into the browser, and provides no control
// capable of enabling Create Delivery / dispatch — mirrors the same
// discipline `ShippingAdmin.jsx`'s CredentialsDialog already established.
// =============================================================================

function PickupConfigCard() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    line1: "", line2: "", city: "", state: "", postal_code: "", country: "US",
    lat: "", lng: "", max_radius_km: "", instructions: "",
    origin_contact_name: "", origin_phone: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/retail/delivery-config");
      const c = data?.config || {};
      setCfg(c);
      const origin = c.origin_address || {};
      setForm({
        line1: origin.line1 || "", line2: origin.line2 || "", city: origin.city || "",
        state: origin.state || "", postal_code: origin.postal_code || "", country: origin.country || "US",
        lat: c.origin_lat ?? "", lng: c.origin_lng ?? "", max_radius_km: c.max_radius_km ?? "",
        instructions: origin.instructions || "",
        origin_contact_name: c.origin_contact_name || "", origin_phone: c.origin_phone || "",
      });
    } catch (e) { toast.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const origin_address = {
        line1: form.line1 || null, line2: form.line2 || null, city: form.city || null,
        state: form.state || null, postal_code: form.postal_code || null,
        country: form.country || "US", instructions: form.instructions || null,
      };
      const body = {
        origin_address,
        origin_lat: form.lat === "" ? null : Number(form.lat),
        origin_lng: form.lng === "" ? null : Number(form.lng),
        max_radius_km: form.max_radius_km === "" ? null : Number(form.max_radius_km),
        origin_contact_name: form.origin_contact_name || null,
        origin_phone: form.origin_phone || null,
      };
      const { data } = await api.put("/retail/delivery-config", body);
      setCfg(data?.config || null);
      toast.success("Pickup configuration saved");
    } catch (e) { toast.error(e); }
    finally { setSaving(false); }
  };

  return (
    <Card data-testid="local-delivery-pickup-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <MapPin className="w-4 h-4 text-indigo-600" />
          Pickup Location &amp; Contact
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="text-sm text-slate-500 flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label className="text-xs font-medium text-slate-600">Address line 1</label>
                <Input value={form.line1} onChange={(e) => setField("line1", e.target.value)} data-testid="ld-origin-line1" />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs font-medium text-slate-600">Address line 2 (optional)</label>
                <Input value={form.line2} onChange={(e) => setField("line2", e.target.value)} />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">City</label>
                <Input value={form.city} onChange={(e) => setField("city", e.target.value)} />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">State</label>
                <Input value={form.state} onChange={(e) => setField("state", e.target.value)} />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Postal code</label>
                <Input value={form.postal_code} onChange={(e) => setField("postal_code", e.target.value)} />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Country</label>
                <Input value={form.country} onChange={(e) => setField("country", e.target.value)} />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Latitude</label>
                <Input value={form.lat} onChange={(e) => setField("lat", e.target.value)} />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Longitude</label>
                <Input value={form.lng} onChange={(e) => setField("lng", e.target.value)} />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Max delivery radius (km)</label>
                <Input value={form.max_radius_km} onChange={(e) => setField("max_radius_km", e.target.value)} />
              </div>
            </div>

            <div className="pt-2 border-t border-slate-100">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                Pickup contact (used only for a courier's Create Delivery request)
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-600">Pickup contact name</label>
                  <Input
                    value={form.origin_contact_name}
                    onChange={(e) => setField("origin_contact_name", e.target.value)}
                    placeholder="Defaults to your business name if left blank"
                    data-testid="ld-origin-contact-name"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600">Pickup phone (E.164, e.g. +15125551234)</label>
                  <Input
                    value={form.origin_phone}
                    onChange={(e) => setField("origin_phone", e.target.value)}
                    placeholder="+15125551234"
                    data-testid="ld-origin-phone"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs font-medium text-slate-600">Pickup instructions (optional)</label>
                  <Input value={form.instructions} onChange={(e) => setField("instructions", e.target.value)} />
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <Button onClick={save} disabled={saving} data-testid="ld-save-pickup-btn">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : null}
                Save
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

const CREDENTIAL_FIELDS = [
  { key: "customer_id", label: "TEST Customer ID", presenceKey: "test_customer_id_present" },
  { key: "client_id", label: "TEST Client ID", presenceKey: "test_client_id_present" },
  { key: "client_secret", label: "TEST Client Secret", presenceKey: "test_client_secret_present" },
  // `payloadField: "signing_key"` — the backend's public request field is
  // named `signing_key` (not `webhook_signing_key`); see `routes.py`'s
  // `SaveUberDirectCredentialsIn` for why. The status RESPONSE still uses
  // the fully descriptive `webhook_signing_key_present` key (unaffected).
  { key: "webhook_signing_key", label: "Webhook Signing Key (optional)", presenceKey: "webhook_signing_key_present", payloadField: "signing_key" },
];

function UberDirectCredentialsDialog({ open, onOpenChange, status, onSaved }) {
  const [fields, setFields] = useState({});
  const [reveal, setReveal] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setFields({}); setReveal({}); }
  }, [open]);

  const setField = (k, v) => setFields((f) => ({ ...f, [k]: v }));
  const toggleReveal = (k) => setReveal((r) => ({ ...r, [k]: !r[k] }));

  const submit = async () => {
    setSaving(true);
    try {
      const body = {};
      for (const f of CREDENTIAL_FIELDS) {
        if (fields[f.key]) body[f.payloadField || f.key] = fields[f.key];
      }
      if (!Object.keys(body).length) {
        toast.error("Enter at least one credential field.");
        setSaving(false);
        return;
      }
      await api.put("/retail/local-delivery/provider-config/uber-direct/credentials", body);
      toast.success("TEST credentials saved (encrypted at rest)");
      setFields({});
      setReveal({});
      onSaved?.();
      onOpenChange(false);
    } catch (e) { toast.error(e); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="uber-direct-credentials-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-indigo-600" />
            Configure Uber Direct TEST Credentials
          </DialogTitle>
          <DialogDescription>
            Environment is fixed to <strong>TEST</strong>. Values are encrypted at rest and
            never echoed back. Leave a field blank to keep its current saved value.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {CREDENTIAL_FIELDS.map((f) => {
            const shown = reveal[f.key];
            const alreadySet = status?.[f.presenceKey];
            return (
              <div key={f.key}>
                <label className="text-xs font-medium text-slate-600 flex items-center gap-1.5">
                  {f.label}
                  {alreadySet && <Badge variant="outline" className="text-[10px]">saved</Badge>}
                </label>
                <div className="relative">
                  <Input
                    type={shown ? "text" : "password"}
                    autoComplete="off"
                    spellCheck={false}
                    value={fields[f.key] || ""}
                    onChange={(e) => setField(f.key, e.target.value)}
                    placeholder={alreadySet ? "•••••••• (leave blank to keep current)" : "Enter value"}
                    data-testid={`uber-direct-creds-input-${f.key}`}
                    className="pr-10 font-mono text-xs"
                  />
                  <button
                    type="button"
                    onClick={() => toggleReveal(f.key)}
                    className="absolute inset-y-0 right-0 px-3 text-slate-400 hover:text-slate-700"
                    data-testid={`uber-direct-creds-reveal-${f.key}`}
                  >
                    {shown ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="uber-direct-creds-save-btn">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : null}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// Local Delivery master switch (provider-neutral) — Phase 3A.6 correction.
// -----------------------------------------------------------------------------
// Distinct from the Uber Direct card below: this controls Local Delivery as a
// WHOLE tenant capability, independent of which courier is configured
// underneath it. A future second provider would share this same switch.
// Deliberately its own card/API call (GET/PUT /retail/local-delivery/config)
// rather than a field inside the Uber Direct card — see
// `provider_config_service.py::update_master_enabled`'s docstring.
// =============================================================================

function LocalDeliveryMasterCard() {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/retail/local-delivery/config");
      setEnabled(!!data?.local_delivery_enabled);
    } catch (e) { toast.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (v) => {
    setBusy(true);
    try {
      const { data } = await api.put("/retail/local-delivery/config", { local_delivery_enabled: v });
      setEnabled(!!data?.local_delivery_enabled);
      toast.success(v ? "Local Delivery enabled" : "Local Delivery disabled");
    } catch (e) { toast.error(e); }
    finally { setBusy(false); }
  };

  return (
    <Card data-testid="local-delivery-master-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <ToggleLeft className="w-4 h-4 text-indigo-600" />
          Local Delivery
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading ? (
          <div className="text-sm text-slate-500 flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : (
          <div className="flex items-center justify-between py-1">
            <div>
              <div className="text-sm font-medium text-slate-800">Enable Local Delivery</div>
              <p className="text-xs text-slate-500 mt-0.5 max-w-md">
                Master switch for this tenant's Local Delivery workflow. Courier providers
                and quote/dispatch capabilities are controlled separately below.
              </p>
            </div>
            <Switch
              checked={enabled}
              onCheckedChange={toggle}
              disabled={busy}
              data-testid="local-delivery-master-switch"
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function UberDirectConfigCard() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const [confirmTestDispatch, setConfirmTestDispatch] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/retail/local-delivery/provider-config/uber-direct");
      setStatus(data);
    } catch (e) { toast.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setFlag = async (patch) => {
    setBusy(true);
    try {
      const { data } = await api.put("/retail/local-delivery/provider-config/uber-direct/flags", patch);
      setStatus(data);
      toast.success("Configuration updated");
    } catch (e) { toast.error(e); }
    finally { setBusy(false); }
  };

  // Tenant-owned SANDBOX dispatch (Aug 2026 sandbox-validation-blocker
  // correction) — completely separate control/endpoint from every
  // switch above; never touches `dispatch_enabled`/`dispatch_requires_
  // authorization` (the platform-managed production pair). The backend
  // (`provider_config_service.py::update_test_dispatch_enabled`) is the
  // authoritative gate; this only sends the toggle and surfaces
  // whatever reason it rejects with.
  const setTestDispatchEnabled = async (enabled) => {
    setBusy(true);
    try {
      const { data } = await api.put("/retail/local-delivery/provider-config/uber-direct/test-dispatch", { enabled });
      setStatus(data);
      toast.success(enabled ? "TEST Dispatch enabled — sandbox deliveries only" : "TEST Dispatch disabled");
      setConfirmTestDispatch(false);
    } catch (e) { toast.error(e); }
    finally { setBusy(false); }
  };

  // Gate C5A-3 (Sep 2026) — the two quote policies. Both have been
  // stored and enforced since long before this control existed
  // (`quote_service.request_quote` refuses below the minimum before any
  // Uber call, and above the cap after it), but no route could write
  // them, so the numbers deciding whether a customer's order is refused
  // were reachable only by a direct database write. Meanwhile the
  // Catalog Fulfillment panel told the tenant the cap was "configured on
  // the Local Delivery page" — this page — where no such field existed.
  //
  // Held as raw strings so a half-typed value is not re-rendered while
  // the tenant is still typing; coerced once, at save. Empty means "no
  // limit", which is a real value the server stores as null.
  const [feeCap, setFeeCap] = useState("");
  const [minOrder, setMinOrder] = useState("");
  const [policySaving, setPolicySaving] = useState(false);

  useEffect(() => {
    if (!status) return;
    setFeeCap(status.max_delivery_fee == null ? "" : String(status.max_delivery_fee));
    setMinOrder(status.min_order_value == null ? "" : String(status.min_order_value));
    // Intentionally keyed on the two values rather than the whole
    // `status` object: re-seeding these inputs on every unrelated status
    // refresh (a flag toggle, a credential save) would discard what the
    // tenant is mid-way through typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status?.max_delivery_fee, status?.min_order_value]);

  const policyDirty =
    (feeCap.trim() === "" ? null : Number(feeCap)) !==
      (status?.max_delivery_fee ?? null)
    || (minOrder.trim() === "" ? null : Number(minOrder)) !==
      (status?.min_order_value ?? null);

  const saveQuotePolicies = async () => {
    const parse = (raw, label) => {
      const t = String(raw).trim();
      if (t === "") return null;              // explicit "no limit"
      const n = Number(t);
      if (!Number.isFinite(n) || n < 0) {
        throw new Error(`${label} must be a dollar amount, or blank for no limit.`);
      }
      return Math.round(n * 100) / 100;
    };
    let payload;
    try {
      payload = {
        max_delivery_fee: parse(feeCap, "Maximum delivery fee"),
        min_order_value: parse(minOrder, "Minimum order value"),
      };
    } catch (e) { toast.error(e.message); return; }

    setPolicySaving(true);
    try {
      const { data } = await api.put(
        "/retail/local-delivery/provider-config/uber-direct/quote-policies", payload,
      );
      // Verify the server actually stored what was submitted before
      // claiming success — the same rule Gate C5A-1 had to add after a
      // silently-dropped field produced a false "Saved" toast.
      const back = {
        max_delivery_fee: data?.max_delivery_fee ?? null,
        min_order_value: data?.min_order_value ?? null,
      };
      if (back.max_delivery_fee !== payload.max_delivery_fee
          || back.min_order_value !== payload.min_order_value) {
        toast.error("Save did not persist — please try again.");
        return;
      }
      setStatus(data);
      toast.success("Delivery limits saved");
    } catch (e) { toast.error(e); }
    finally { setPolicySaving(false); }
  };

  const disconnect = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete("/retail/local-delivery/provider-config/uber-direct");
      setStatus(data);
      toast.success("Uber Direct TEST configuration removed");
      setConfirmDisconnect(false);
    } catch (e) { toast.error(e); }
    finally { setBusy(false); }
  };

  const credsComplete = status?.test_customer_id_present && status?.test_client_id_present && status?.test_client_secret_present;
  // Environment-aware wording (Aug 2026, tenant-owned production
  // dispatch pass) — `mode` can only ever become "live" via the
  // documented manual production-activation runbook (a direct database
  // write outside this application entirely; no route here, or
  // anywhere in this codebase, can set it — see `provider_config_
  // service.py::save_test_credentials`, which unconditionally forces
  // "sandbox"). This page must therefore DETECT live mode and adapt
  // its own wording/controls to it, never be the thing that causes it.
  const isLive = status?.mode === "live";
  // `dispatch_operational` (backend-computed, `provider_config_service.
  // py::_safe_status`) is the authoritative readiness signal — combines
  // `dispatch_enabled` + the tenant flag mirror + `dispatch_requires_
  // authorization === false`, exactly what `tenant_acceptance_service.
  // py`'s own precondition check requires before "Accept Order &
  // Request Uber" can ever succeed. Never approximated client-side.
  const dispatchOperational = !!status?.dispatch_operational;

  return (
    <Card data-testid="local-delivery-uber-direct-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base justify-between">
          <span className="flex items-center gap-2">
            <Truck className="w-4 h-4 text-indigo-600" />
            Uber Direct
          </span>
          <Badge
            variant="outline"
            className={`text-[11px] ${isLive ? "border-emerald-300 text-emerald-700 bg-emerald-50" : ""}`}
            data-testid="uber-direct-environment-badge"
          >
            Environment: {isLive ? "LIVE" : "TEST"}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading || !status ? (
          <div className="text-sm text-slate-500 flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : (
          <>
            {!isLive && (
              <div
                className={`p-3 rounded-lg border text-xs flex items-start gap-2 ${status.test_dispatch_enabled ? "bg-indigo-50 border-indigo-200 text-indigo-800" : "bg-rose-50 border-rose-200 text-rose-800"}`}
                data-testid="uber-direct-dispatch-disabled-banner"
              >
                <Ban className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold tracking-wide">
                    {status.test_dispatch_enabled ? "TEST DISPATCH ENABLED — SANDBOX DELIVERIES ONLY" : "TEST — QUOTES ARE SANDBOX-ONLY"}
                  </div>
                  <div className="mt-0.5">
                    {status.test_dispatch_enabled
                      ? "Accepted Local Delivery orders create a real Uber sandbox test delivery — never a live courier, never a real charge to Uber. This is completely isolated from production."
                      : <>Delivery requests made in this environment never reach a real courier. {status.dispatch_disabled_reason}</>}
                  </div>
                </div>
              </div>
            )}
            {isLive && (
              <div
                className={`p-3 rounded-lg border text-xs flex items-start gap-2 ${dispatchOperational ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-amber-50 border-amber-200 text-amber-800"}`}
                data-testid="uber-direct-live-status-banner"
              >
                <ShieldCheck className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold tracking-wide">
                    LIVE — DISPATCH {dispatchOperational ? "IS ACTIVE" : "IS NOT YET ACTIVE"}
                  </div>
                  <div className="mt-0.5">
                    {dispatchOperational
                      ? "Paid Local Delivery orders can be accepted and dispatched to a real courier from Retail Orders."
                      : "Production credentials are configured, but dispatch has not been activated for this tenant yet. This is done by platform support via the production activation runbook — it is not a self-service toggle."}
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3 text-xs">
              <StatusRow label={isLive ? "Production Customer ID" : "TEST Customer ID"} present={status.test_customer_id_present} />
              <StatusRow label={isLive ? "Production Client ID" : "TEST Client ID"} present={status.test_client_id_present} />
              <StatusRow label={isLive ? "Production Client Secret" : "TEST Client Secret"} present={status.test_client_secret_present} />
              <StatusRow label="Webhook Signing Key" present={status.webhook_signing_key_present} />
            </div>

            {/* Readiness summary — never a raw internal field name,
                always a plain business answer plus the reason when not
                ready. */}
            <div className="rounded-md border border-slate-200 p-3 text-xs space-y-1.5" data-testid="uber-direct-readiness-summary">
              <ReadinessRow
                label="Ready to quote"
                ready={!!status.quote_enabled}
                reason={!credsComplete ? "Missing one or more credentials." : !status.provider_configured ? "Provider is not enabled." : null}
              />
              <ReadinessRow
                label="Ready to dispatch"
                ready={isLive ? dispatchOperational : !!status.test_dispatch_ready}
                reason={
                  !isLive
                    ? (status.test_dispatch_ready
                        ? null
                        : status.test_dispatch_not_ready_reason
                          || "Turn on TEST Dispatch below once quotes are ready.")
                    : !status.dispatch_enabled
                    ? "Production credentials are set, but this tenant has not been activated for dispatch yet."
                    : null
                }
              />
            </div>

            <div className="flex items-center justify-between py-1">
              <div>
                <div className="text-sm font-medium text-slate-800">Enable Uber Direct provider (this tenant only)</div>
                <div className="text-xs text-slate-500">Connects this courier provider. Separate from the Local Delivery master switch above — both must be on for quotes to work. Does not enable quotes or dispatch by itself.</div>
              </div>
              <Switch
                checked={!!status.provider_configured}
                onCheckedChange={(v) => setFlag({ provider_enabled: v })}
                disabled={busy}
                data-testid="uber-direct-provider-enabled-switch"
              />
            </div>

            <div className="flex items-center justify-between py-1">
              <div>
                <div className="text-sm font-medium text-slate-800">Enable Quotes</div>
                <div className="text-xs text-slate-500">
                  {credsComplete ? "Ready — all credentials are present." : "Requires all three credentials to be saved first."}
                </div>
              </div>
              <Switch
                checked={!!status.quote_enabled}
                onCheckedChange={(v) => setFlag({ quote_enabled: v })}
                disabled={busy || (!status.quote_enabled && !credsComplete)}
                data-testid="uber-direct-quote-enabled-switch"
              />
            </div>

            {isLive ? (
              <div className="flex items-center justify-between py-1 opacity-60">
                <div>
                  <div className="text-sm font-medium text-slate-800">Dispatch</div>
                  <div className="text-xs text-slate-500">
                    Activated by platform support only — not a self-service control on this page.
                  </div>
                </div>
                <Switch checked={dispatchOperational} disabled data-testid="uber-direct-dispatch-enabled-switch-locked" />
              </div>
            ) : status.app_environment === "production" ? (
              // Tenant-owned SANDBOX dispatch (Aug 2026) — the control
              // is never rendered as an actionable switch when this
              // application instance's own deployment environment is
              // production, even for a tenant still in TEST/sandbox
              // mode: `update_test_dispatch_enabled` would authoritatively
              // reject it server-side regardless, so this avoids
              // presenting a control that can never succeed here.
              <div className="flex items-center justify-between py-1 opacity-60">
                <div>
                  <div className="text-sm font-medium text-slate-800">TEST Dispatch</div>
                  <div className="text-xs text-slate-500">
                    Not available — this application instance is a production deployment.
                  </div>
                </div>
                <Switch checked={false} disabled data-testid="uber-direct-test-dispatch-switch-locked" />
              </div>
            ) : (
              <div className="flex items-center justify-between py-1">
                <div>
                  <div className="text-sm font-medium text-slate-800">TEST Dispatch — sandbox deliveries only</div>
                  <div className="text-xs text-slate-500">
                    {status.test_dispatch_enabled
                      ? "Accepted orders create a real Uber sandbox delivery. Never live, never billed."
                      : status.test_dispatch_not_ready_reason || "Ready — turning this on lets accepted orders request a sandbox Uber delivery."}
                  </div>
                </div>
                <Switch
                  checked={!!status.test_dispatch_enabled}
                  onCheckedChange={(v) => {
                    if (v) { setConfirmTestDispatch(true); } else { setTestDispatchEnabled(false); }
                  }}
                  disabled={busy || (!status.test_dispatch_enabled && !!status.test_dispatch_not_ready_reason)}
                  data-testid="uber-direct-test-dispatch-switch"
                />
              </div>
            )}

            {/* Gate C5A-3 — the tenant's own quote limits. TENANT-scoped,
                and labelled as such: `quote_service.request_quote` reads
                both from this tenant's provider config with no catalog
                dimension, so every catalog using Uber Direct is judged
                against the same pair. A tenant who assumed per-catalog
                would set it once and be surprised everywhere else. */}
            <div className="pt-3 border-t border-slate-100 space-y-3" data-testid="uber-direct-quote-policies">
              <div>
                <div className="text-sm font-medium text-slate-800">Delivery limits</div>
                <p className="text-xs text-slate-500">
                  Applies to <strong>every catalog</strong> on this account that uses Uber Direct — these are
                  account-level limits, not per-catalog. Leave a box empty for no limit.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-600" htmlFor="uber-max-delivery-fee">
                    Maximum delivery fee ($)
                  </label>
                  <input
                    id="uber-max-delivery-fee"
                    type="number" min="0" step="0.01" inputMode="decimal"
                    value={feeCap}
                    onChange={(e) => setFeeCap(e.target.value)}
                    data-testid="uber-direct-max-delivery-fee-input"
                    className="mt-1 w-full h-9 rounded-md border border-slate-300 px-2 text-sm"
                    placeholder="No limit"
                  />
                  <p className="mt-1 text-xs text-slate-500">
                    If Uber quotes more than this, the order is not offered delivery. Checked after Uber returns a price.
                  </p>
                </div>
                <div>
                  <label className="text-xs text-slate-600" htmlFor="uber-min-order-value">
                    Minimum order value ($)
                  </label>
                  <input
                    id="uber-min-order-value"
                    type="number" min="0" step="0.01" inputMode="decimal"
                    value={minOrder}
                    onChange={(e) => setMinOrder(e.target.value)}
                    data-testid="uber-direct-min-order-value-input"
                    className="mt-1 w-full h-9 rounded-md border border-slate-300 px-2 text-sm"
                    placeholder="No minimum"
                  />
                  <p className="mt-1 text-xs text-slate-500">
                    Orders below this are asked to add more before checkout. This is not the Own Delivery charge or the
                    free-delivery threshold in Commerce Settings, and not the delivery radius above.
                  </p>
                </div>
              </div>
              <div className="flex justify-end">
                <Button
                  onClick={saveQuotePolicies}
                  disabled={policySaving || !policyDirty}
                  data-testid="uber-direct-quote-policies-save"
                >
                  {policySaving ? "Saving\u2026" : policyDirty ? "Save limits" : "Saved"}
                </Button>
              </div>
            </div>

            {/* Tenant-owned production dispatch (Aug 2026) — a LIVE
                tenant's real production credentials must never be
                editable/removable through the TEST-only credential
                form or the disconnect action below: that form's own
                write path unconditionally forces `mode: "sandbox"` on
                save (see `provider_config_service.py::
                save_test_credentials`), which would silently downgrade
                a live tenant back to TEST, and disconnect would delete
                the live credentials outright. Both are hidden once
                `mode === "live"`. */}
            {!isLive && (
              <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                <Button
                  variant="outline"
                  onClick={() => setDialogOpen(true)}
                  data-testid="uber-direct-configure-creds-btn"
                >
                  <KeyRound className="w-3.5 h-3.5 mr-1.5" />
                  {credsComplete ? "Update TEST Credentials" : "Save TEST Credentials"}
                </Button>
                <Button
                  variant="ghost"
                  className="text-rose-600 hover:text-rose-700"
                  onClick={() => setConfirmDisconnect(true)}
                  data-testid="uber-direct-disconnect-btn"
                >
                  Disconnect TEST Configuration
                </Button>
              </div>
            )}
            {isLive && (
              <div className="pt-2 border-t border-slate-100 text-xs text-slate-500" data-testid="uber-direct-live-credentials-notice">
                Production credentials are managed by platform support, not from this page —
                contact support to make changes.
              </div>
            )}
          </>
        )}
      </CardContent>

      <UberDirectCredentialsDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        status={status}
        onSaved={load}
      />

      <Dialog open={confirmTestDispatch} onOpenChange={setConfirmTestDispatch}>
        <DialogContent data-testid="uber-direct-test-dispatch-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Enable TEST Dispatch?</DialogTitle>
            <DialogDescription>
              Accepted Local Delivery orders will create a real Uber sandbox test delivery —
              never a live courier, never a real charge. This only ever works in this
              non-production TEST environment.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmTestDispatch(false)}>Cancel</Button>
            <Button onClick={() => setTestDispatchEnabled(true)} disabled={busy} data-testid="uber-direct-test-dispatch-confirm-btn">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : null}
              Enable TEST Dispatch
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmDisconnect} onOpenChange={setConfirmDisconnect}>
        <DialogContent data-testid="uber-direct-disconnect-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Disconnect Uber Direct TEST configuration?</DialogTitle>
            <DialogDescription>
              This permanently deletes the stored encrypted TEST credentials for this tenant
              and turns off quotes and dispatch. This cannot be undone from the UI — you would
              need to re-enter credentials to reconnect.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmDisconnect(false)}>Cancel</Button>
            <Button variant="destructive" onClick={disconnect} disabled={busy} data-testid="uber-direct-disconnect-confirm-btn">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : null}
              Disconnect
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function ReadinessRow({ label, ready, reason }) {
  return (
    <div className="flex items-start gap-1.5" data-testid={`readiness-row-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      {ready ? (
        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0 mt-0.5" />
      ) : (
        <XCircle className="w-3.5 h-3.5 text-slate-300 flex-shrink-0 mt-0.5" />
      )}
      <div>
        <span className={ready ? "text-slate-700 font-medium" : "text-slate-500 font-medium"}>{label}</span>
        {!ready && reason && <span className="text-slate-400"> — {reason}</span>}
      </div>
    </div>
  );
}

function StatusRow({ label, present }) {
  return (
    <div className="flex items-center gap-1.5" data-testid={`status-row-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      {present ? (
        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
      ) : (
        <XCircle className="w-3.5 h-3.5 text-slate-300" />
      )}
      <span className={present ? "text-slate-700" : "text-slate-400"}>{label}</span>
    </div>
  );
}

export default function LocalDeliveryAdminPage() {
  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto py-8 px-4 space-y-6">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-indigo-600" />
            Local Delivery — Provider Configuration
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Configure where deliveries originate and, separately, which courier provider fulfills
            them. Production ("live") activation is not available from this page.
          </p>
        </div>
        <PickupConfigCard />
        <LocalDeliveryMasterCard />
        <UberDirectConfigCard />
      </div>
    </DashboardLayout>
  );
}
